import { useCallback, useEffect, useRef } from 'react';

import { Maybe } from 'types/UtilityTypes';

import { BATCH_LTL_SAVE_DEBOUNCE_MS } from '../constants';
import { BatchLTLPersistedState } from '../types';
import {
  clearBatchLTLState,
  saveBatchLTLState,
} from '../utils/batchLTLStorage';

type UsePersistedBatchLTLStateParams = {
  storageKey: Maybe<string>;
  threadId: string;
  // Null while there is nothing worth saving yet.
  stateToSave: Maybe<Omit<BatchLTLPersistedState, 'savedAt'>>;
  // Changes when something must be saved at once (a job started, a reply went out).
  urgentKey: string;
};

// Keeps the rep's batch work saved as they go: edits after a short pause, job ids and replies
// right away, and whatever is still pending when they leave the email.
export function usePersistedBatchLTLState({
  storageKey,
  threadId,
  stateToSave,
  urgentKey,
}: UsePersistedBatchLTLStateParams) {
  // Last urgent key written, so only real changes skip the debounce.
  const lastUrgentKeyRef = useRef(urgentKey);
  // Unsaved state waiting on the debounce; flushed if the panel unmounts first.
  const pendingSaveRef = useRef<Maybe<() => void>>(null);
  // Set by clear() so the unmount flush does not write the batch back.
  const isClearedRef = useRef(false);

  // Save after each change: immediately for urgent changes, otherwise debounced.
  useEffect(() => {
    if (!storageKey || !stateToSave || isClearedRef.current) {
      return;
    }

    const save = () => {
      pendingSaveRef.current = null;
      saveBatchLTLState(storageKey, threadId, {
        ...stateToSave,
        savedAt: Date.now(),
      });
    };

    if (urgentKey !== lastUrgentKeyRef.current) {
      lastUrgentKeyRef.current = urgentKey;
      save();
      return;
    }

    pendingSaveRef.current = save;
    const timer = setTimeout(save, BATCH_LTL_SAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [storageKey, threadId, stateToSave, urgentKey]);

  // Leaving the email unmounts the panel; write the last edits instead of dropping them.
  useEffect(
    () => () => {
      if (!isClearedRef.current) {
        pendingSaveRef.current?.();
      }
    },
    []
  );

  const clear = useCallback(() => {
    isClearedRef.current = true;
    pendingSaveRef.current = null;
    if (storageKey) {
      clearBatchLTLState(storageKey);
    }
  }, [storageKey]);

  return { clear };
}
